/**
 * Holding a row in the place you last saw it.
 *
 * A completed task stays on screen until the page reloads, so a misclick on the control you
 * press most often is recoverable. Appending it to the end of the list met that bar and
 * failed the real one: the row you were looking at slid out from under the cursor, and
 * everything below it moved up. Undo you have to go and find is barely undo at all.
 *
 * The same problem, not obviously the same problem, turned up with subtasks (§8.6.3):
 * completing one bubbles progress to its parent, and the open-tasks list sorts stalest
 * first — so a parent that had just received a tick moved to the *end* of its group on the
 * very next fetch, purely because it was now the freshest thing there. It never disappeared
 * or finished, so nothing about it looked like a completion; it just weren't where you were
 * looking anymore. Held entries fix that too, for a row that stays present but needs pinning
 * in place rather than one that vanished and needs reinserting — the same anchor, applied to
 * both.
 *
 * So a held row is anchored to the task it *followed* rather than to an index. Anchors
 * survive the list being re-sorted, re-grouped or re-fetched, which indices do not — and
 * the list is re-fetched on every completion.
 */

import { type Task } from "../api";

export type HeldTask = {
  /** Identifies the row. Its own fields (status, subtasks, …) are read from the fresh list
   * when present there — this exists to say "hold this id at this anchor", not to freeze a
   * snapshot; `task` supplies the data only when the fresh fetch no longer has it (the
   * completed-and-gone case) and otherwise contributes `group`/`days_since_touch` alone, so
   * a row pinned in a section it has since left (e.g. no longer slipping) still reads as
   * belonging there rather than jumping mid-render. */
  task: Task;
  /** The id this row sat after, within its own group. `null` means it was first. */
  after: string | null;
};

/** The task `task` follows in `list`, skipping anything in a different group. */
export function anchorFor(list: Task[], task: Task): string | null {
  const group = task.group ?? null;
  const index = list.findIndex((t) => t.id === task.id);
  if (index < 0) return null;
  for (let i = index - 1; i >= 0; i--) {
    if ((list[i].group ?? null) === group) return list[i].id;
  }
  return null;
}

/**
 * Put held tasks back where they were.
 *
 * A held id present in `list` is pulled out of wherever the fresh sort put it and reinserted
 * at its anchor instead, using the fresh copy (current status, subtasks, …) with the held
 * entry's own `group`/`days_since_touch` carried over — the row is current, its *section*
 * isn't renegotiated mid-hold. A held id absent from `list` (genuinely completed and gone
 * from an open-tasks fetch) is reinserted using the held snapshot outright, same as before.
 *
 * Anything whose anchor has since gone — deleted, or filtered out by the energy slider —
 * goes to the end rather than being dropped, because a row you cannot see is a row you
 * cannot undo.
 */
export function mergeHeld(list: Task[], held: HeldTask[]): Task[] {
  const byId = new Map(list.map((t) => [t.id, t]));
  const heldIds = new Set(held.map((h) => h.task.id));
  const rest = list.filter((t) => !heldIds.has(t.id));
  const pending = [...held];
  const result: Task[] = [];

  const resolve = (entry: HeldTask): Task => {
    const fresh = byId.get(entry.task.id);
    return fresh ? { ...fresh, group: entry.task.group, days_since_touch: entry.task.days_since_touch } : entry.task;
  };

  const drain = (after: string | null) => {
    for (let i = 0; i < pending.length; i++) {
      if (pending[i].after !== after) continue;
      const [entry] = pending.splice(i, 1);
      const task = resolve(entry);
      result.push(task);
      drain(task.id); // a row held behind another held row
      i = -1; // the array shifted under us
    }
  };

  drain(null);
  for (const task of rest) {
    result.push(task);
    drain(task.id);
  }
  result.push(...pending.map(resolve));
  return result;
}
